import type { FormRegularStep } from "./form-types";

/**
 * Derives the "Lead Form Step" `user_selection` object for a step from the
 * machine's persistedFieldValues and postalLocationValues.
 *
 * Returns a field-keyed Record covering the non-PII inputs on the step:
 *
 * - radioInput:      { [fieldName]: resolvedLabel }
 *   Resolves the persisted raw value back through options to
 *   metricsLabel ?? value ?? label (matching the order used at selection time).
 *
 * - multiSelectInput: { [fieldName]: string[] }
 *   Each selected option resolves to metricsLabel ?? value ?? label.
 *   Selections are persisted as `${fieldName}.${optionValue} = "on"`.
 *
 * - standardInput (postalCode): { postalCode: value, franchiseNumber?: value }
 *   Reads postalLocationValues.postalCode and the selected office's organization
 *   number persisted under "franchiseNumber" (see PostalCodeField).
 *
 * PII text fields (firstName, lastName, email, phone) are intentionally
 * excluded — do not add a generic persistedFieldValues read for standardInput
 * types other than postalCode.
 *
 * Fields with empty string values are omitted.
 * Returns {} when no tracked values are found.
 */
function getStepUserSelection(
  step: FormRegularStep,
  persistedFieldValues: Record<string, string>,
  postalLocationValues: { postalCode: string; location: string },
): Record<string, string | string[]> {
  const result: Record<string, string | string[]> = {};

  for (const input of step.inputs ?? []) {
    if (input._type === "radioInput") {
      const { fieldName, options } = input;
      if (!fieldName) {
        continue;
      }
      const storedValue = persistedFieldValues[fieldName];
      if (!storedValue) {
        continue;
      }
      const matchedOption = (options ?? []).find(
        (opt) => (opt.value ?? opt.label) === storedValue,
      );
      const resolved = matchedOption
        ? (matchedOption.metricsLabel ??
          matchedOption.value ??
          matchedOption.label ??
          storedValue)
        : storedValue;
      result[fieldName] = resolved;
      continue;
    }

    if (input._type === "multiSelectInput") {
      const { fieldName, options } = input;
      if (!fieldName) {
        continue;
      }
      const selected: string[] = [];
      for (const option of options ?? []) {
        const optionValue = option.value ?? option.label ?? option._key;
        if (persistedFieldValues[`${fieldName}.${optionValue}`] === "on") {
          selected.push(
            option.metricsLabel ?? option.value ?? option.label ?? "",
          );
        }
      }
      if (selected.length > 0) {
        result[fieldName] = selected;
      }
      continue;
    }

    if (input._type === "standardInput") {
      const inputType = input.inputType as string | null | undefined;
      if (!inputType) {
        continue;
      }

      if (inputType === "postalCode") {
        const postalCode = postalLocationValues.postalCode;
        if (postalCode) {
          result.postalCode = postalCode;
        }
        const franchiseNumber = persistedFieldValues.franchiseNumber;
        if (franchiseNumber) {
          result.franchiseNumber = franchiseNumber;
        }
        continue;
      }

      // PII fields (firstName, lastName, email, phone) and other text inputs
      // are intentionally NOT included in user_selection.
    }
  }

  return result;
}

export { getStepUserSelection };
